import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from 'src/environments/environment';
import { AppService } from '../core/services';
import { LoginBaseComponent } from '../login-base.component';

/**
 * Direct-URL fallback login for non-SSO admins (roma users, the
 * superadmin/test account, break-glass during OAM outage).
 *
 * Renders an email + password form right inside admin-frontend.
 * Credentials are POSTed to Keycloak's token endpoint (OAuth2 Password
 * Grant) and the resulting access token is injected into the
 * keycloak-angular instance — same pattern the embedded-view path
 * uses in auth-guard.guard.ts.
 *
 * The form is gated by a frontend allowlist
 * (`environment.fallbackLoginAllowedEmails`) so users who aren't
 * explicitly permitted can't even attempt this flow. The list is a UX
 * gate — server-side controls (KC user existence + AdminUser doc) are
 * what actually enforce access.
 *
 * When `environment.enableFallbackLogin` is false, the whole route
 * bounces back to `/login` (SSO-only).
 *
 * Logo and cover image loading are handled by LoginBaseComponent.
 */
@Component({
  selector: 'app-login-admin',
  templateUrl: './login-admin.component.html',
  styleUrls: ['./login-admin.component.scss'],
  standalone: false
})
export class LoginAdminComponent extends LoginBaseComponent implements OnInit {
  email = '';
  // Bound via [(ngModel)]; left uninitialised so there is no empty-password
  // literal (Fortify: Password Management: Empty Password). ngModel populates it.
  password!: string;
  errorMessage = '';
  isSubmitting = false;

  constructor(
    private readonly keycloak: KeycloakService,
    private readonly router: Router,
    private readonly appService: AppService
  ) {
    super();
  }

  ngOnInit(): void {
    void this.bootstrap();
    this.loadAssets();
  }

  private async bootstrap(): Promise<void> {
    if (!environment.enableFallbackLogin) {
      await this.router.navigate(['/login']);
      return;
    }

    if (this.keycloak.isLoggedIn()) {
      await this.router.navigate(['/dashboard']);
    }
  }

  async submit(): Promise<void> {
    this.errorMessage = '';

    const email = this.email.trim().toLowerCase();
    const password = this.password;

    if (!email || !password) {
      this.errorMessage = 'Enter your email and password.';
      return;
    }

    const allowlist = resolveFallbackAllowedEmails(environment).map((e) =>
      e.toLowerCase()
    );
    if (allowlist.length === 0 || !allowlist.includes(email)) {
      this.errorMessage =
        'This account is not permitted to sign in via the admin login. Use SSO instead.';
      return;
    }

    this.isSubmitting = true;
    try {
      const { url, realm, clientId } = environment.keycloakConfig;
      const tokenUrl = `${url}/realms/${realm}/protocol/openid-connect/token`;

      const body = new URLSearchParams();
      body.set('grant_type', 'password');
      body.set('client_id', clientId);
      body.set('username', email);
      body.set('password', password);
      body.set('scope', 'openid');

      const headers = new Headers();
      headers.set('Content-Type', 'application/x-www-form-urlencoded');

      const res = await fetch(tokenUrl, {
        method: 'POST',
        headers,
        body: body.toString()
      });

      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as Record<
          string,
          unknown
        > | null;
        const description = detail?.['error_description'];
        const error = detail?.['error'];
        const reason =
          (typeof description === 'string' && description) ||
          (typeof error === 'string' && error) ||
          '';
        this.errorMessage =
          res.status === 401
            ? 'Invalid email or password.'
            : `Login failed${reason ? `: ${reason}` : ''}.`;
        return;
      }

      const tokenResp = (await res.json()) as Record<string, unknown>;
      this.injectTokenIntoKeycloak(tokenResp);
      // AuthGuard reads routeAccessList synchronously, so we must
      // populate it BEFORE the navigation fires — otherwise hasAccess
      // returns false on the dashboard route and redirects to
      // /unauthorize. Mirrors the embedded-view path in
      // auth-guard.guard.ts.
      this.appService.checkRoles();
      await this.router.navigateByUrl('/dashboard');
    } catch (_err) {
      this.errorMessage = 'Could not reach Keycloak. Try again in a moment.';
    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Push the token issued by Keycloak's password-grant endpoint into
   * the keycloak-angular instance so existing interceptors / guards
   * treat the user as authenticated. Mirrors the embedded-view pattern
   * in auth-guard.guard.ts.
   */
  private injectTokenIntoKeycloak(tokenResp: Record<string, unknown>): void {
    const kc = this.keycloak.getKeycloakInstance() as unknown as Record<
      string,
      unknown
    >;
    const accessToken = String(tokenResp['access_token'] ?? '');
    const refreshToken = String(tokenResp['refresh_token'] ?? '');
    const rawIdToken = tokenResp['id_token'];
    const idToken = typeof rawIdToken === 'string' ? rawIdToken : undefined;
    const parsed = decodeJwt(accessToken);

    kc['token'] = accessToken;
    kc['tokenParsed'] = parsed;
    kc['refreshToken'] = refreshToken;
    kc['refreshTokenParsed'] = decodeJwt(refreshToken);
    if (idToken) {
      kc['idToken'] = idToken;
      kc['idTokenParsed'] = decodeJwt(idToken);
    }
    kc['authenticated'] = true;
    kc['resourceAccess'] = parsed['resource_access'];
    kc['realmAccess'] = parsed['realm_access'];
    kc['sessionId'] = parsed['sid'];
    kc['subject'] = parsed['sub'];
  }
}

/**
 * Decode the payload section of a JWT without validating the
 * signature — used only to populate the in-memory keycloak instance
 * for the embedded auth path. The backend re-validates the signature
 * + iss + expiry on every API call.
 */
function decodeJwt(jwt: string): Record<string, unknown> {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) {
      return {};
    }
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function resolveFallbackAllowedEmails(env: {
  fallbackLoginAllowedEmails?: string[];
}): string[] {
  return env.fallbackLoginAllowedEmails ?? [];
}
